// hooks/usePosts.ts
import { useState, useMemo, useCallback, useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { postsService } from "../services/postsService";
import {
  Post,
  PostStatus,
  StatusStatisticsResponse,
} from "@/core/types/postTypes";
import { PostItem, ValidStatuses } from "@/core/Post/PostItem.model";
import { useAppStore } from "@/core/store/useAppStore";
import { usePostsQuery } from "./usePostsQuery";
import { excludeStories } from "./excludeStories";
import { postKeys } from "@/core/queryClient";
import {
  PostStage,
  STAGE_CONFIGS,
  getStageConfig,
  getStatisticsOrder,
} from "@/domains/PostCreator/utils/stageConfig";

// Re-export so existing consumers can import excludeStories from usePosts.
// (It lives in its own module to avoid pulling axios into the jest runtime.)
export { excludeStories } from "./excludeStories";

interface UsePostsOptions {
  gridId?: number;
  autoRefresh?: boolean;
  status?: PostStatus;
  stage?: PostStage;
}

// Helper function to convert PostItems to legacy Post format for compatibility
const convertPostItemsToLegacyPosts = (postItems: PostItem[]): Post[] => {
  return postItems.map(item => item.toLegacyPost());
};

/**
 * Primary hook for posts - uses React Query for server state
 * Maintains same interface as before for backward compatibility
 */
export const usePosts = (options: UsePostsOptions = {}) => {
  const selectedClient = useAppStore((state) => state.selectedClient);
  const queryClient = useQueryClient();

  // Use React Query for data fetching
  const {
    data: rawPostItems = [],
    isLoading,
    error,
  } = usePostsQuery(selectedClient?.id);

  // Stories share the posts table but must never appear on the Posts board.
  // Strip them once, up front, so both the stage list and the stage counts
  // stay story-free.
  const postItems = useMemo(
    () => excludeStories(rawPostItems),
    [rawPostItems],
  );

  // Filter and sort posts based on options
  const posts = useMemo(() => {
    const statusOrder = getStatisticsOrder();

    // Filter by stage if specified
    const stageStatuses = options.stage
      ? getStageConfig(options.stage).statuses
      : null;

    const filteredPostItems = stageStatuses
      ? postItems.filter(item => stageStatuses.includes(item.status as ValidStatuses))
      : postItems;

    // Sort by status order
    const sortedPostItems = [...filteredPostItems].sort((a, b) => {
      const indexA = statusOrder.indexOf(a.status);
      const indexB = statusOrder.indexOf(b.status);

      const orderA = indexA === -1 ? statusOrder.length : indexA;
      const orderB = indexB === -1 ? statusOrder.length : indexB;

      return orderA - orderB;
    });

    return convertPostItemsToLegacyPosts(sortedPostItems);
  }, [postItems, options.stage]);

  // Stage counts come from the UNFILTERED list — `posts` is already
  // narrowed to the active stage, so counting there would make every
  // tab's badge change whenever the active tab changes.
  const stageCounts = useMemo(
    () => STAGE_CONFIGS.reduce(
      (acc, stage) => {
        acc[stage.id] = postItems.filter((item) =>
          stage.statuses.includes(item.status as ValidStatuses)
        ).length;
        return acc;
      },
      {} as Record<PostStage, number>
    ),
    [postItems]
  );

  const refetch = useCallback(() => {
    if (!selectedClient?.id) return;
    queryClient.invalidateQueries({ queryKey: postKeys.all(selectedClient.id) });
  }, [selectedClient?.id, queryClient]);

  return {
    posts,
    isLoading,
    error: error ? (error instanceof Error ? error.message : "Failed to load posts") : null,
    refetch,
    // Computed values
    totalPosts: posts.length,
    carouselPosts: posts.filter((post) => post.isCarousel).length,
    // Status-based computed values
    postsByStatus: (status: PostStatus) =>
      posts.filter((post) => post.status === status).length,
    // Stage-based computed values
    postsByStage: (stage: PostStage) => {
      const stageConfig = getStageConfig(stage);
      return posts.filter((post) => stageConfig.statuses.includes(post.status))
        .length;
    },
    stageCounts,
  };
};

// Rest of the hooks remain the same...
export const useStatusStatistics = () => {
  const { selectedClient } = useAppStore();
  const [statistics, setStatistics] = useState<StatusStatisticsResponse | null>(
    null,
  );
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchStatistics = async () => {
    if (!selectedClient?.id) {
      setStatistics(null);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const stats = await postsService.getStatusStatistics(selectedClient.id);
      setStatistics(stats);
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "Failed to fetch statistics";
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  // Auto-fetch on client change with a staleness guard: when selectedClient
  // flips (e.g. a stale localStorage client → the URL's client), the previous
  // in-flight request must NOT clobber state when it resolves last. Without
  // this, a 404 ("Client not found") from a since-deselected client lands on
  // top of the current client's good data and shows a false error screen.
  const clientId = selectedClient?.id;
  useEffect(() => {
    if (!clientId) {
      setStatistics(null);
      setError(null);
      return;
    }
    let active = true;
    setIsLoading(true);
    setError(null);
    postsService
      .getStatusStatistics(clientId)
      .then((stats) => {
        if (active) setStatistics(stats);
      })
      .catch((err) => {
        if (active)
          setError(err instanceof Error ? err.message : "Failed to fetch statistics");
      })
      .finally(() => {
        if (active) setIsLoading(false);
      });
    return () => {
      active = false;
    };
  }, [clientId]);

  return {
    statistics,
    isLoading,
    error,
    refetch: fetchStatistics,
  };
};

export const useClientPosts = () => {
  return usePosts({});
};

export const useStageBasedPosts = () => {
  const [activeStage, setActiveStage] = useState<PostStage | null>("draft");

  const postsData = usePosts({
    stage: activeStage || undefined,
  });

  return {
    ...postsData,
    activeStage,
    setActiveStage,
  };
};
